import { mapGetters, mapState } from 'vuex';
import ApiService from '@/services/apiService';
import multiSearchMixin from '@/mixins/multiSearchVirtualDevices';
import healthStatusMixin from '@/components/core/virtualDevice/healthStatusMixin';
import { waitForRef } from '@/utils/vuex';
const MANAGE_DEVICES_LIMIT = 500;
export default {
  name: 'ViewVirtualDevicesList',
  mixins: [healthStatusMixin, multiSearchMixin],
  data() {
    return {
      showDialogStatistics: false,
      showExportDialog: false,
      // only show "map" button if the current user has access to ANY devices with geo-coordinates
      hasAnyGeoDevices: false,
      favouriteFilter: false,
      showDialogCreate: false,
      accountFilterReady: false,
      showDialogEditAll: false,
      setSelectedAccounts: [],
      showBulkImportDialog: false,
      // can't use mapGetters as these need to be mutated at times
      initOperator: this.$store.getters['app/getVirtualDeviceListSearchOperator'],
      initSearchItems: this.$store.getters['app/getVirtualDeviceListSearchItems'],
      selectedRows: [],
      allResultRows: [],
      // moved bunch of items for search in `multiSearchMixin`

      // Bulk edit
      showBulkEditAllDialog: false,
      isCalculating: false,
      isCalculatingError: false,
      belongToSameAccount: false,
      groupsTags: null,
      totalCountItems: null,
      listIsReadyToFetch: false,
      searchInitialized: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    ...mapGetters({
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    showEditSelectedBtn() {
      return this.permissions['all.manage'] || this.permissions['VirtualDevice.manage'] || this.permissions['VirtualDevice.write'];
    },
    isDeviceManager() {
      return this.permissions['VirtualDevice.manage'];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    statisticsSearchTypes() {
      return this.searchTypes.filter(st => st.param !== 'zone');
    },
    bulkEditAllBtnDisabledTooltip() {
      return this.isBulkEditAllBtnDisabled ? this.$i18n_t('DigitalTwin.VirtualDevice.bulkEditAlert', {
        n: MANAGE_DEVICES_LIMIT
      }) : '';
    },
    isBulkEditAllBtnDisabled() {
      if (this.selectedRows.length) {
        return this.selectedRows.length > MANAGE_DEVICES_LIMIT;
      } else {
        return !this.totalCountItems || this.totalCountItems === 0 || this.totalCountItems > MANAGE_DEVICES_LIMIT;
      }
    },
    devices() {
      if (this.selectedRows && this.selectedRows.length > 0) {
        return this.selectedRows;
      }
      return this.allResultRows || [];
    },
    isDeviceDriverActive() {
      return !this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback');
    }
  },
  watch: {
    searchItems(newValue) {
      if (this.searchInitialized && Array.isArray(newValue) && newValue.length > 0) {
        this.$store.dispatch('app/updateVirtualDeviceListSearch', {
          items: newValue
        });
      }
    },
    searchOperator(newValue) {
      if (this.searchInitialized) {
        this.$store.dispatch('app/updateVirtualDeviceListSearch', {
          operator: newValue
        });
      }
    }
    // language-check to update search-types in searchTypesMixin
  },
  created() {
    const {
      view,
      mode
    } = this.$route.query;
    if (view) {
      this.listIsReadyToFetch = false;
      this.setUpMonitoringViewList(view, mode);
    } else {
      this.initOperator = this.$store.getters['app/getVirtualDeviceListSearchOperator'];
      this.initSearchItems = this.$store.getters['app/getVirtualDeviceListSearchItems'];
      this.listIsReadyToFetch = true;
    }
  },
  mounted() {
    ApiService.virtualDeviceCheckForGeoClusters(this).then(res => {
      this.hasAnyGeoDevices = res.hasGeoDevices;
    }).catch(error => {
      console.error(error);
    });
  },
  methods: {
    setUpMonitoringViewList(viewId, mode) {
      // Update the store before rendering the child
      ApiService.virtualDeviceViewGet(this, viewId).then(async data => {
        const search = data.viewConfig.searchItems;
        this.initOperator = data.viewConfig.searchOperator;
        // transform to legacy format
        this.initSearchItems = search.map(item => ({
          param: item.variable,
          key: item.key,
          search: item.search,
          condition: item.condition
        }));
        if (mode === 'warnings') {
          this.initSearchItems.push({
            param: 'health',
            search: {
              value: '1',
              text: 'Warning'
            }
          });
        }
        if (mode === 'errors') {
          this.initSearchItems.push({
            param: 'health',
            search: {
              value: '2',
              text: 'Alert'
            }
          });
        }
        const accounts = [data.tenantId];
        this.setSelectedAccounts = accounts;

        // Persist search items and operator in store, then render child
        return this.$store.dispatch('app/updateVirtualDeviceListSearch', {
          items: this.initSearchItems,
          operator: this.initOperator
        }).then(() => {
          this.listIsReadyToFetch = true;
          this.searchInitialized = true;
          waitForRef(this, '$refs.list.$refs.multiSearch').then(ref => {
            const items = this.initSearchItems.map(item => {
              var _item$search;
              return {
                field: item.param,
                condition: item.param && item.condition,
                key: item.key,
                value: ((_item$search = item.search) === null || _item$search === void 0 ? void 0 : _item$search.value) || item.search
              };
            });
            ref.loadRules({
              rules: items,
              join: this.initOperator
            });
          }).catch(error => {
            console.error(error);
          });
        });
      }).catch(() => {
        this.listIsReadyToFetch = true;
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.loadMonitoringViewFailed'), 'error');
      });
    },
    updateList() {
      this.$refs.list.fetchList();
    },
    updateRowsSelected(rows) {
      if (Array.isArray(rows)) {
        this.selectedRows = rows;
      } else if (rows) {
        this.selectedRows = [rows];
      } else {
        this.selectedRows = [];
      }
    },
    checkDevicesAccount() {
      const deviceIds = this.devices.map(item => item.id);
      if (!deviceIds.length) {
        this.isCalculating = false;
        this.isCalculatingError = false;
        return;
      }
      this.isCalculating = true;
      const payload = {
        virtualDevicesIds: deviceIds
      };
      ApiService.precalculationBackgroundRunner(payload).then(result => {
        this.isCalculating = false;
        this.belongToSameAccount = result.data.belongToSameAccount;
      }).catch(error => {
        console.error(error);
        this.isCalculatingError = true;
        this.isCalculating = false;
      });
    },
    finishedCreating() {
      this.showDialogCreate = false;
      this.updateList();
    },
    async showBulkEditDialog() {
      this.isCalculatingError = false;
      if (this.selectedRows.length === 0 && this.totalCountItems > 0 && this.allResultRows.length === 0) {
        await this.getSearchedResults();
      }
      this.checkDevicesAccount();
      this.showBulkEditAllDialog = true;
    },
    closeDialog() {
      this.selectedRows = [];
      this.showBulkEditAllDialog = false;
      this.$emit('close-step-form');
      this.updateList();
      this.getSearchedResults();
    },
    closeDialogBulkImport() {
      this.showBulkImportDialog = false;
    },
    setItemsResults(items) {
      this.allResultRows = items.virtualDevices || [];
    },
    async getSearchedResults() {
      try {
        const result = await this.$refs.list.fetchResult(true, 'load-all');
        this.setItemsResults(result);
        return result;
      } catch (e) {
        console.error(e);
      }
    },
    setTotalCountItems(payload) {
      this.totalCountItems = payload.totalCount;
      this.allResultRows = [];
    },
    showDialogBulkImport() {
      this.showBulkImportDialog = true;
    }
  }
};